import { useRef, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { users, games, type ProfileDeck, type PodracerGame } from '@/lib/api';
import { toast } from '@/lib/toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AnalysisText } from '@/components/AnalysisText';
import { useFlightAnimation } from '@/lib/useFlightAnimation';
import { VisibilityToggle } from '@/components/VisibilityToggle';
import { ColorIdentityPips } from '@/components/ColorIdentityPips';
import { ManaCurveSparkline } from '@/components/ManaCurveSparkline';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { cardImageUris, normalizeColorIdentity } from '@/lib/utils';
import { WUBRG_ORDER } from '@/lib/constants';
import { BracketBadge } from '@/components/BracketBadge';
import { useAuth } from '@/lib/auth-context';
type SortCol = 'name' | 'commander' | 'colors' | 'curve' | 'combos' | 'bracket' | 'visibility' | 'updated';

function getCurveAvg(deck: ProfileDeck): number {
  const mc = deck.latest_analysis?.power_level?.mana_curve;
  if (!mc) return Infinity;
  let total = 0, weighted = 0;
  for (let i = 0; i <= 7; i++) { const c = mc[String(i)] ?? 0; total += c; weighted += i * c; }
  return total > 0 ? weighted / total : Infinity;
}
type SortDir = 'asc' | 'desc';

function getBracket(deck: ProfileDeck): number | null {
  return deck.latest_analysis?.power_level?.wotc_bracket ?? null;
}

/** Score color identity for sort: colorless=0, then pip count, ties broken by WUBRG index sum */
function colorSortKey(colors: string[]): number {
  if (colors.length === 0) return -1;
  const indexSum = colors.reduce((acc, c) => acc + WUBRG_ORDER.indexOf(c), 0);
  return colors.length * 100 + indexSum;
}

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (col !== sortCol) {
    return <span className="ml-1 opacity-25 text-[10px]">⇅</span>;
  }
  return <span className="ml-1 opacity-80 text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function DeckTable({ decks, isOwner }: { decks: ProfileDeck[]; isOwner: boolean }) {
  const { hasFeature } = useAuth();
  const [visibilityOverrides, setVisibilityOverrides] = useState<Record<string, string>>({});
  const [sortCol, setSortCol] = useState<SortCol>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    return [...decks].sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortCol === 'commander') {
        const ac = (a.commanders ?? [])[0] ?? '';
        const bc = (b.commanders ?? [])[0] ?? '';
        cmp = ac.localeCompare(bc);
      } else if (sortCol === 'curve') {
        cmp = getCurveAvg(a) - getCurveAvg(b);
      } else if (sortCol === 'colors') {
        cmp = colorSortKey(normalizeColorIdentity(a.color_identity)) -
              colorSortKey(normalizeColorIdentity(b.color_identity));
      } else if (sortCol === 'combos') {
        const ac = a.latest_analysis?.power_level?.combo_analysis?.total_combos ?? -1;
        const bc = b.latest_analysis?.power_level?.combo_analysis?.total_combos ?? -1;
        cmp = ac - bc;
      } else if (sortCol === 'bracket') {
        const ab = getBracket(a) ?? 99;
        const bb = getBracket(b) ?? 99;
        cmp = ab - bb;
      } else if (sortCol === 'visibility') {
        const av = visibilityOverrides[a.id] ?? a.visibility ?? 'unlisted';
        const bv = visibilityOverrides[b.id] ?? b.visibility ?? 'unlisted';
        cmp = av.localeCompare(bv);
      } else if (sortCol === 'updated') {
        const at = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bt = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        cmp = at - bt;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [decks, sortCol, sortDir, visibilityOverrides]);

  if (decks.length === 0) {
    return (
      <div className="text-center text-on-surface-variant p-12">
        No public decks yet.
      </div>
    );
  }

  const thClass = "px-4 py-3 text-sm font-semibold text-on-surface-variant cursor-pointer select-none hover:text-on-surface transition-colors";

  return (
    <div className="bg-surface-container rounded-md overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-outline-variant">
            <th className={`${thClass} text-left`} onClick={() => handleSort('name')}>
              Name <SortIcon col="name" sortCol={sortCol} sortDir={sortDir} />
            </th>
            <th className={`${thClass} text-left`} onClick={() => handleSort('commander')}>
              Commander <SortIcon col="commander" sortCol={sortCol} sortDir={sortDir} />
            </th>
            <th className={`${thClass} text-left w-28`} onClick={() => handleSort('colors')}>
              Colors <SortIcon col="colors" sortCol={sortCol} sortDir={sortDir} />
            </th>
            <th className={`${thClass} text-center w-28 hidden md:table-cell`} onClick={() => handleSort('curve')}>
              Curve <SortIcon col="curve" sortCol={sortCol} sortDir={sortDir} />
            </th>
            <th className={`${thClass} text-center w-20 hidden md:table-cell`} onClick={() => handleSort('combos')}>
              Combos <SortIcon col="combos" sortCol={sortCol} sortDir={sortDir} />
            </th>
            <th className={`${thClass} text-center w-20`} onClick={() => handleSort('bracket')}>
              Bracket <SortIcon col="bracket" sortCol={sortCol} sortDir={sortDir} />
            </th>
            {hasFeature('elo') && (
              <th className={`${thClass} text-center w-24`}>Record</th>
            )}
            <th className={`${thClass} text-right w-32`} onClick={() => handleSort('updated')}>
              Modified <SortIcon col="updated" sortCol={sortCol} sortDir={sortDir} />
            </th>
            {isOwner && (
              <th className={`${thClass} text-right w-32`} onClick={() => handleSort('visibility')}>
                Visibility <SortIcon col="visibility" sortCol={sortCol} sortDir={sortDir} />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map((deck, i) => {
            const commanders = deck.commanders ?? [];
            const colors = normalizeColorIdentity(deck.color_identity);
            const bracket = getBracket(deck);
            const vis = visibilityOverrides[deck.id] ?? deck.visibility ?? 'unlisted';

            return (
              <tr
                key={deck.id}
                className={`border-b border-outline-variant/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-surface-container-high/30'}`}
              >
                {/* Name */}
                <td className="px-4 py-3">
                  <Link
                    to={`/deck/${deck.id}`}
                    className="text-on-surface font-medium text-sm no-underline hover:text-primary transition-colors"
                  >
                    {deck.name}
                  </Link>
                </td>

                {/* Commander */}
                <td className="px-4 py-3">
                  {commanders.length > 0 ? (
                    <span className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                      {commanders.map((cmd, ci) => (
                        <span key={cmd} className="inline-flex items-center gap-1">
                          <span
                            className="decklist-card text-sm cursor-help"
                            data-card-name={cmd}
                            onMouseEnter={(e) => showCardTooltip(cardImageUris(cmd), e)}
                            onMouseLeave={hideCardTooltip}
                            onFocus={(e) => showCardTooltip(cardImageUris(cmd), e)}
                            onBlur={hideCardTooltip}
                            tabIndex={0}
                          >
                            {cmd}
                          </span>
                          {ci < commanders.length - 1 && (
                            <span className="text-on-surface-variant opacity-50">+</span>
                          )}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-on-surface-variant text-sm opacity-50">—</span>
                  )}
                </td>

                {/* Color Identity */}
                <td className="px-4 py-3">
                  {colors.length > 0 ? (
                    <ColorIdentityPips colors={colors} size="sm" />
                  ) : (
                    <span className="text-on-surface-variant text-xs opacity-40">—</span>
                  )}
                </td>

                {/* Mana Curve sparkline */}
                <td className="px-4 py-3 text-center hidden md:table-cell">
                  {(() => {
                    const mc = deck.latest_analysis?.power_level?.mana_curve;
                    if (!mc) return <span className="text-on-surface-variant text-xs opacity-40">—</span>;
                    const dist = Array.from({ length: 8 }, (_, i) => mc[String(i)] ?? 0);
                    return dist.some(v => v > 0)
                      ? <ManaCurveSparkline curve={dist} width={80} height={24} />
                      : <span className="text-on-surface-variant text-xs opacity-40">—</span>;
                  })()}
                </td>

                {/* Combos */}
                <td className="px-4 py-3 text-center hidden md:table-cell">
                  {(() => {
                    const n = deck.latest_analysis?.power_level?.combo_analysis?.total_combos;
                    if (n == null) return <span className="text-on-surface-variant text-xs opacity-40">—</span>;
                    return (
                      <span className={`text-sm font-medium ${n >= 5 ? 'text-red-400' : n >= 2 ? 'text-yellow-400' : 'text-on-surface-variant'}`}>
                        {n}
                      </span>
                    );
                  })()}
                </td>

                {/* Bracket */}
                <td className="px-4 py-3 text-center">
                  {bracket != null ? (
                    <BracketBadge
                      bracket={bracket}
                      variant="pill"
                      powerLevel={deck.latest_analysis?.power_level}
                    />
                  ) : (
                    <span className="text-on-surface-variant text-xs opacity-40">—</span>
                  )}
                </td>

                {/* W/L/D record (elo flag) */}
                {hasFeature('elo') && (
                  <td className="px-4 py-3 text-center">
                    {deck.game_stats && deck.game_stats.total > 0 ? (
                      <span className="text-xs text-on-surface-variant font-medium">
                        {deck.game_stats.wins}W / {deck.game_stats.losses}L
                      </span>
                    ) : (
                      <span className="text-on-surface-variant text-xs opacity-40">—</span>
                    )}
                  </td>
                )}

                {/* Last modified */}
                <td className="px-4 py-3 text-right">
                  {deck.updated_at ? (
                    <span className="text-xs text-on-surface-variant" title={new Date(deck.updated_at).toLocaleString()}>
                      {new Date(deck.updated_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-on-surface-variant text-xs opacity-40">—</span>
                  )}
                </td>

                {/* Visibility (owner only) */}
                {isOwner && (
                  <td className="px-4 py-3 text-right">
                    <VisibilityToggle
                      deckId={deck.id}
                      currentVisibility={vis}
                      onChanged={(newVis) => setVisibilityOverrides(prev => ({ ...prev, [deck.id]: newVis }))}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Pod History ───────────────────────────────────────────────────────────────

function formatGameOutcome(game: PodracerGame): string {
  if (game.outcome === 'draw') return 'Draw';
  const match = game.outcome.match(/^winner:(\d)$/);
  if (match) {
    const idx = parseInt(match[1]);
    const player = game.players[idx];
    if (player) {
      const cmdName = player.commander
        ? (player.commander.split(/[,/]/)[0] ?? player.name)
        : player.name;
      return `${cmdName} wins`;
    }
  }
  return game.outcome;
}

function PodHistorySection({
  gamesList,
  onVisibilityChange,
}: {
  gamesList: PodracerGame[];
  onVisibilityChange?: (gameId: string, isPublic: boolean) => Promise<void>;
}) {
  const [toggling, setToggling] = useState<string | null>(null);

  if (gamesList.length === 0) {
    return (
      <div className="mb-8 bg-surface-container rounded-md overflow-hidden">
        <div className="px-5 py-3.5 flex items-center gap-3">
          <span className="material-symbols-outlined text-on-surface-variant text-[20px] leading-none">swords</span>
          <span className="font-semibold text-md text-on-surface">Pod History</span>
        </div>
        <div className="px-5 pb-4 text-sm text-on-surface-variant opacity-70 border-t border-outline-variant pt-4">
          No games recorded yet. Save a game from the Podracer life counter to see it here.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 bg-surface-container rounded-md overflow-hidden">
      <div className="px-5 py-3.5 flex items-center gap-2">
        <span className="material-symbols-outlined text-on-surface-variant text-[20px] leading-none">swords</span>
        <span className="font-semibold text-md text-on-surface">Pod History</span>
        <span className="ml-1 text-xs text-on-surface-variant bg-white/10 rounded-full px-2 py-0.5">
          {gamesList.length}
        </span>
      </div>
      <div className="border-t border-outline-variant">
        {gamesList.map((game) => (
          <div
            key={game.id}
            className="flex items-center gap-3 px-5 py-3 border-b border-outline-variant/40 last:border-b-0 hover:bg-white/3 transition-colors"
          >
            {/* Date */}
            <span className="text-xs text-on-surface-variant shrink-0 w-20">
              {new Date(game.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>

            {/* Outcome */}
            <span className="flex-1 text-sm text-on-surface truncate">
              {formatGameOutcome(game)}
              {' '}
              <span className="text-on-surface-variant text-xs">
                —{' '}
                {game.players.map((p, pi) => {
                  const cmdName = (p.commander || p.name).split(/[,/]/)[0].trim();
                  return p.deck_id ? (
                    <Link
                      key={pi}
                      to={`/deck/${p.deck_id}`}
                      className="text-primary hover:underline"
                    >
                      {cmdName}
                    </Link>
                  ) : (
                    <span key={pi}>{cmdName}</span>
                  );
                }).flatMap((el, i) => i === 0 ? [el] : [<span key={`sep-${i}`}>, </span>, el])}
              </span>
            </span>

            {/* Final lives */}
            <div className="hidden sm:flex gap-1 shrink-0">
              {game.final_lives.map((l, i) => (
                <span
                  key={i}
                  className="text-[11px] tabular-nums px-1.5 py-0.5 rounded bg-white/8 text-on-surface-variant font-mono"
                >
                  {l}
                </span>
              ))}
            </div>

            {/* Public toggle (owner only) */}
            {onVisibilityChange && <button
              type="button"
              title={game.is_public ? 'Make private' : 'Make public'}
              disabled={toggling === game.id}
              onClick={async () => {
                setToggling(game.id);
                try {
                  await onVisibilityChange(game.id, !game.is_public);
                } finally {
                  setToggling(null);
                }
              }}
              className="shrink-0 text-on-surface-variant hover:text-on-surface transition-colors bg-transparent border-none cursor-pointer disabled:opacity-40 p-1"
            >
              <span className="material-icons text-[18px] leading-none">
                {game.is_public ? 'lock_open' : 'lock'}
              </span>
            </button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProfileView() {
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const { user: authUser, hasFeature } = useAuth();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [roast, setRoast] = useState<string | null>(null);
  const [roastPublished, setRoastPublished] = useState<boolean | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const roastButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fire } = useFlightAnimation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['profile', userId],
    queryFn: () => users.getProfile(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const { data: gamesData, refetch: refetchGames } = useQuery({
    queryKey: ['games', 'mine'],
    queryFn: () => games.list(),
    enabled: data?.is_own_profile === true,
    staleTime: 30_000,
  });

  const roastMutation = useMutation({
    mutationFn: (force: boolean) => users.generateRoast(userId!, force),
    onSuccess: (result) => {
      setRoast(result.roast);
      setRoastPublished(result.published);
      void queryClient.invalidateQueries({ queryKey: ['profile', userId] });
    },
    onError: () => toast.error('Failed to generate roast'),
  });

  usePageTitle(data?.user?.display_name ?? 'Profile');

  const topCommanders = useMemo(() => {
    const stats = data?.commander_stats ?? {};
    return Object.entries(stats)
      .filter(([, s]) => s.total > 0)
      .map(([name, s]) => ({
        name,
        wins: s.wins,
        losses: s.losses,
        winPct: Math.round((s.wins / s.total) * 100),
      }))
      .sort((a, b) => b.winPct - a.winPct)
      .slice(0, 5);
  }, [data?.commander_stats]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <span className="text-on-surface-variant">Loading profile…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center p-16">
        <p className="text-error-text">User not found.</p>
        <Link to="/decks" className="text-primary">Browse decks</Link>
      </div>
    );
  }

  const { user, decks } = data;

  // Merge server state with local state (local takes precedence after first interaction)
  const displayRoast = roast ?? data.roast ?? null;
  const isPublished = roastPublished ?? data.roast_published ?? false;
  const isOwnProfile = data.is_own_profile === true;
  const displayAvatarUrl = avatarUrl ?? user.avatar_url ?? null;

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setAvatarUploading(true);
    try {
      const result = await users.uploadAvatar(file);
      setAvatarUrl(result.avatar_url);
      await queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      toast.success('Avatar updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload avatar';
      toast.error(message);
    } finally {
      setAvatarUploading(false);
    }
  };

  const startEditing = () => {
    setEditName(user.display_name);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditName('');
  };

  const saveDisplayName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === user.display_name) {
      cancelEditing();
      return;
    }
    setSaving(true);
    try {
      await users.updateDisplayName(trimmed);
      await queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      setEditing(false);
      toast.success('Display name updated');
    } catch {
      toast.error('Failed to update display name');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveDisplayName();
    if (e.key === 'Escape') cancelEditing();
  };

  const handleTogglePublish = async () => {
    setPublishLoading(true);
    try {
      const result = await users.toggleRoastPublish(user.id);
      setRoastPublished(result.published);
      toast.success(result.published ? 'Roast published to your profile' : 'Roast unpublished');
    } catch {
      toast.error('Failed to update roast visibility');
    } finally {
      setPublishLoading(false);
    }
  };

  const roleBadge = user.role === 'admin' || user.role === 'influencer' ? (
    <span className={`badge badge-${user.role} ml-2`}>
      {user.role}
    </span>
  ) : null;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8 p-6 bg-surface-container rounded-md">
        {/* Avatar — clickable for own profile */}
        <div className="shrink-0 flex flex-col items-center gap-1.5">
          {isOwnProfile && (
            <input
              ref={fileInputRef}
              id="avatar-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
              onChange={handleAvatarFileChange}
            />
          )}
          <label
            htmlFor={isOwnProfile ? 'avatar-file-input' : undefined}
            className={`relative group block w-16 h-16 rounded-full overflow-hidden ${isOwnProfile ? 'cursor-pointer' : ''}`}
            title={isOwnProfile ? 'Click to change avatar' : undefined}
          >
            {displayAvatarUrl ? (
              <img
                src={displayAvatarUrl}
                alt={user.display_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary flex items-center justify-center text-[28px] font-bold text-on-primary">
                {user.display_name?.[0]?.toUpperCase() ?? '?'}
              </div>
            )}
            {/* Spinner overlay while uploading */}
            {avatarUploading && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                <span className="material-icons text-white animate-spin text-[20px]">refresh</span>
              </div>
            )}
            {/* Camera icon overlay on hover (own profile only) */}
            {isOwnProfile && !avatarUploading && (
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="material-icons text-white text-[22px]">photo_camera</span>
              </div>
            )}
          </label>
          {isOwnProfile && (
            <label
              htmlFor="avatar-file-input"
              className={`text-xs text-primary hover:underline cursor-pointer leading-none ${avatarUploading ? 'opacity-40 pointer-events-none' : ''}`}
            >
              {avatarUploading ? 'Uploading…' : 'Change photo'}
            </label>
          )}
        </div>
        <div className="flex-1">
          {editing ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={saving}
                className="bg-surface border border-outline-variant rounded-sm text-on-surface text-xl font-semibold py-1.5 px-3 outline-none min-w-[180px]"
              />
              <button
                onClick={saveDisplayName}
                disabled={saving}
                className={`bg-primary text-on-primary border-none rounded-sm py-1.5 px-4 text-base font-semibold cursor-pointer ${
                  saving ? 'opacity-60' : 'opacity-100'
                }`}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={cancelEditing}
                disabled={saving}
                className="bg-transparent text-on-surface-variant border border-outline-variant rounded-sm py-1.5 px-3 text-base cursor-pointer"
              >
                Cancel
              </button>
            </div>
          ) : (
            <h1 className="text-on-surface text-3xl font-bold m-0 mb-1 flex items-center">
              {user.display_name}
              {roleBadge}
              {data.is_own_profile && (
                <button
                  onClick={startEditing}
                  title="Edit display name"
                  className="bg-transparent border-none text-on-surface-variant cursor-pointer ml-2 text-lg py-0.5 px-1.5 rounded-sm leading-none"
                >
                  &#9998;
                </button>
              )}
            </h1>
          )}
          <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: editing ? 8 : 0 }}>
            <span className="text-base text-on-surface-variant">
              {data.deck_count} public deck{data.deck_count !== 1 ? 's' : ''}
            </span>
            {hasFeature('elo') && user.elo != null && (
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-mono font-semibold ${
                user.elo >= 1200 ? 'bg-yellow-500/20 text-yellow-400' :
                user.elo >= 1000 ? 'bg-slate-500/20 text-slate-300' :
                'bg-slate-700/20 text-slate-500'
              }`}>
                <span className="material-symbols-outlined" style={{ fontSize: 12, lineHeight: 1 }}>trophy</span>
                {user.elo}
              </span>
            )}
          </div>
          {user.created_at && (
            <div className="text-base text-on-surface-variant mt-0.5">
              Joined {new Date(user.created_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Roast section */}
      {(data.is_own_profile || displayRoast) && (
        <div className="mb-8 bg-surface-container rounded-md overflow-hidden">
          <div className="px-5 py-3.5 flex items-center gap-3 flex-wrap">
            <span className="text-lg">🔥</span>
            <span className="font-semibold text-md text-on-surface">Collection Roast</span>
            {data.is_own_profile && displayRoast && (
              <button
                onClick={handleTogglePublish}
                disabled={publishLoading}
                className={`inline-flex items-center gap-1.5 py-1 px-3 rounded-md text-sm font-medium border cursor-pointer disabled:opacity-50 ${
                  isPublished
                    ? 'bg-transparent border-outline-variant text-on-surface-variant'
                    : 'bg-transparent border-primary text-primary'
                }`}
              >
                {publishLoading ? '…' : isPublished ? 'Unpublish' : 'Publish'}
              </button>
            )}
            {data.is_own_profile && (
              <button
                ref={roastButtonRef}
                onClick={() => {
                  fire(roastButtonRef.current);
                  roastMutation.mutate(!!displayRoast);
                }}
                disabled={roastMutation.isPending}
                className="ml-auto inline-flex items-center gap-1.5 py-1.5 px-4 rounded-md bg-primary text-on-primary text-base font-medium border-none cursor-pointer disabled:opacity-50"
              >
                {roastMutation.isPending ? 'Roasting…' : displayRoast ? 'Regenerate' : 'Roast Me'}
              </button>
            )}
          </div>
          {displayRoast && (
            <div className="px-5 pb-5 text-base text-on-surface leading-relaxed border-t border-outline-variant pt-4">
              <AnalysisText text={displayRoast} />
            </div>
          )}
          {data.is_own_profile && !displayRoast && !roastMutation.isPending && (
            <div className="px-5 pb-4 text-sm text-on-surface-variant opacity-70">
              Get an AI-generated read on your playstyle based on your collection. Publish it to share with others.
            </div>
          )}
        </div>
      )}

      {/* Pod History — own profile sees all games with toggle; logged-in viewer with elo flag sees public games */}
      {hasFeature('elo') && (data.is_own_profile || (authUser && data.pod_games && data.pod_games.length > 0)) && (
        <PodHistorySection
          gamesList={data.is_own_profile ? (gamesData?.games ?? data.pod_games ?? []) : (data.pod_games ?? [])}
          onVisibilityChange={data.is_own_profile ? async (gameId, isPublic) => {
            await games.setPublic(gameId, isPublic);
            void refetchGames();
          } : undefined}
        />
      )}

      {/* Decks table */}
      <DeckTable decks={decks} isOwner={data.is_own_profile} />

      {/* Top Commanders by Win Rate */}
      {hasFeature('elo') && topCommanders.length > 0 && (
        <div className="mt-8 bg-surface-container rounded-md p-4">
          <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wide mb-3">
            Top Commanders by Win Rate
          </h3>
          <ol className="flex flex-col gap-2">
            {topCommanders.map((cmd, i) => (
              <li key={cmd.name} className="flex items-center gap-3">
                <span className="text-xs text-on-surface-variant w-4 tabular-nums">{i + 1}.</span>
                <span className="decklist-card flex-1 text-sm font-medium"
                      data-card-name={cmd.name}
                      onMouseEnter={(e) => showCardTooltip(cardImageUris(cmd.name), e)}
                      onMouseLeave={hideCardTooltip}
                >{cmd.name}</span>
                <span className="text-xs text-on-surface-variant">{cmd.wins}W / {cmd.losses}L</span>
                <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>
                  {cmd.winPct}%
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
